import { Button, PlainButton, Typography } from 'omakase';
import { IconArrowRight } from '@tock/icons';
import { OmakaseTypographyVariant } from 'theme/typography/omakaseTypography';
import { assertNever } from '@tock/shared/utils/typeUtils';
import { cardLinkCssDefs } from 'consumer-page-widgets/CardLinkWidget/cardLinkCssDefs';
import { getAutoFormatSrcAndSrcSetForWidthPx } from '@tock/images';
import Anchor from 'components/Anchor';
import React from 'react';

export type CardLinkVariant = 'default' | 'large';

export interface CardLinkProps {
  mainText: string;
  imageUrl: string;
  imageAltText: string;
  href: string;
  linkText: string;
  className?: string;
  variant?: CardLinkVariant;
  onClick?(): void;
}

function getTypographyForVariant(variant: CardLinkVariant): OmakaseTypographyVariant {
  switch (variant) {
    case 'default':
    case 'large':
      return 'heading02';
    default:
      assertNever(variant);
  }
}

const CardLink: React.FunctionComponent<CardLinkProps> = ({
  mainText,
  imageUrl,
  imageAltText,
  href,
  linkText,
  variant = 'default',
  onClick,
}) => {
  const typographyVariant = getTypographyForVariant(variant);

  const { src, srcSet } = getAutoFormatSrcAndSrcSetForWidthPx(
    {
      width: variant === 'large' ? 1000 : 500,
    },
    imageUrl
  );

  return (
    <div css={cardLinkCssDefs.cardContainer}>
      {/* The anchor below should not be in the tabIndex because the PlainButton below goes to
          the same place and is tabbable. */}
      <Anchor
        tabIndex={-1}
        css={cardLinkCssDefs.anchor}
        href={href}
        aria-label={linkText}
        onClick={onClick}
      />
      <div css={cardLinkCssDefs.imageContainerOuter(variant)}>
        <div css={cardLinkCssDefs.imageContainerInner(variant)}>
          <img css={cardLinkCssDefs.image} src={src} srcSet={srcSet} alt={imageAltText} />
        </div>
      </div>
      <div css={cardLinkCssDefs.contentContainer(variant)}>
        <Typography variant={typographyVariant}>{mainText}</Typography>
        <Button
          css={[cardLinkCssDefs.button(variant), cardLinkCssDefs.buttonMobile]}
          component={Anchor}
          href={href}
          size="medium"
          variant="basic"
          iconEnd={<IconArrowRight />}
          onClick={onClick}
        >
          {linkText}
        </Button>
        <PlainButton
          css={[cardLinkCssDefs.button(variant), cardLinkCssDefs.buttonDesktop]}
          component={Anchor}
          href={href}
          size="xlarge"
          variant="basic"
          iconEnd={<IconArrowRight />}
          onClick={onClick}
        >
          {linkText}
        </PlainButton>
      </div>
    </div>
  );
};

export default CardLink;
